import * as React from 'react';
import { cx } from './styles';

interface ICareerSliderControls {
  previous: () => void;
  next: () => void;
}

interface ICareerSliderProps {
  children: (controls: ICareerSliderControls) => React.ReactNode;
}

export const CareerSlider: React.FC<ICareerSliderProps> = ({ children }) => {
  const [current, setCurrent] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const touchStart = React.useRef<number | null>(null);
  const lastDirection = React.useRef<'previous' | 'next' | null>(null);
  const amountRef = React.useRef(0);
  const change = React.useCallback((delta: number): void => {
    if (amountRef.current < 2) {
      return;
    }
    lastDirection.current = delta < 0 ? 'previous' : 'next';
    setCurrent(value => (value + delta + amountRef.current) % amountRef.current);
  }, []);
  const slides = React.Children.toArray(children({ previous: () => change(-1), next: () => change(1) }));
  const amount = slides.length;
  amountRef.current = amount;

  React.useLayoutEffect(() => {
    if (!lastDirection.current) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      rootRef.current
        ?.querySelector<HTMLElement>(
          `[data-slider-position="${current}"] [data-career-slider-direction="${lastDirection.current}"]`,
        )
        ?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [current]);

  if (!amount) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={cx('career-slider')}
      role="region"
      aria-roledescription="карусель"
      aria-label="Ценности Циана"
      onTouchStart={event => {
        touchStart.current = event.touches[0].clientX;
      }}
      onTouchEnd={event => {
        if (touchStart.current !== null) {
          const delta = touchStart.current - event.changedTouches[0].clientX;
          if (Math.abs(delta) > 50) {
            change(delta > 0 ? 1 : -1);
          }
        }
        touchStart.current = null;
      }}
      onKeyDown={event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          change(event.key === 'ArrowRight' ? 1 : -1);
        }
      }}
    >
      <div
        className={cx('slider-track')}
        style={{
          transform: `translate3d(calc(-${current * 100}% - ${current} * var(--career-slider-gap)), 0, 0)`,
        }}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            className={cx('slider-slide')}
            data-slider-position={index}
            role="group"
            aria-roledescription="слайд"
            aria-label={`${index + 1} из ${amount}`}
            aria-hidden={current !== index}
          >
            {slide}
          </div>
        ))}
      </div>
      <span className={cx('sr-only')} aria-live="polite">
        {current + 1} из {amount}
      </span>
    </div>
  );
};
